import {NavBar, Popup, Dialog, Toast} from 'antd-mobile';
import { CloseOutline } from 'antd-mobile-icons';
import { t } from 'i18next';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PickupAppointmentType } from '../graphql/types/PickupTaskWithAppointmentsType';
import MapboxMap from '../../../components/Map/MapboxMapView';
import EmptyState from "../../../components/EmptyState";
import { buildAddressFromAppointment } from '../../../utils/addressUtils';
import useHistoryControl from "../../../hooks/useHistoryControl";
import PickingUpDetailCard, {PickingUpDetailCardRef} from "./components/PickingUpDetailCard";
import { MapBoxPageMarker } from '../../../constants/mapBoxTrackLog';
import { client } from '../../../graphql/client';
import { GET_PUBLISHED_TASK_BY_DRIVER, GetPublishedTaskResponse } from '../graphql/queries/getPublishedTaskByDriverAccountId';
import { GET_PICKUP_TASK_APPOINTMENT_STATUS, GetPickupTaskAppointmentStatusResponse } from '../graphql/queries/getPickupTaskAppointmentStatus';
import { usePickupStore } from '../store/pickupStore';

console.log('🔥🔥🔥 PickingUpMapModePage.tsx 文件已加载 - 版本: 2024-02-14-v3 🔥🔥🔥');

const DEFAULT_MARKER_COLOR = '#000000';

const getStopNumberDisplay = (appointment: PickupAppointmentType): string => {
    if (appointment.status === 'start_point') {
        return t('pages.pickUp.pickingUp.start');
    }
    if (appointment.status === 'end_point') {
        return t('pages.pickUp.pickingUp.end');
    }
    return appointment.sequence.toString();
};

interface PickingUpMapModePageProps { }

const PickingUpMapModePage: React.FC<PickingUpMapModePageProps> = () => {
    console.log('========== PickingUpMapModePage: 组件渲染 ==========');
    
    const navigate = useNavigate();
    const setPickupTaskAppointmentList = usePickupStore(state => state.setPickupTaskAppointmentList);
    
    const [pickingUpData, setPickingUpData] = useState<PickupAppointmentType[]>(() => {
        console.log('PickingUpMapModePage: 初始化 pickingUpData state');
        return [];
    });
    const [loading, setLoading] = useState(false);

    const [pickingUpDataList, setPickingUpDataList] = useState<PickupAppointmentType[]>([]);

    // 直接从接口获取数据
    const fetchPickupTaskAppointmentList = useCallback(async (signal?: AbortSignal) => {
        console.log('🔥🔥🔥 PickingUpMapModePage: ✅ 开始从接口获取数据 🔥🔥🔥');
        console.log('🔥 使用 fetchPolicy: network-only，不使用缓存');
        setLoading(true);
        try {
            const { data } = await client.query<GetPublishedTaskResponse>({
                query: GET_PUBLISHED_TASK_BY_DRIVER,
                fetchPolicy: 'network-only',
                context: {
                    fetchOptions: {
                        signal: signal
                    }
                }
            });
            
            // 检查是否被取消
            if (signal?.aborted) {
                console.log('🔥 请求已被取消，不更新状态');
                return;
            }
            
            const appointmentList = data?.getPublishedTaskByDriver || [];
            console.log('🔥🔥🔥 PickingUpMapModePage: ✅ 接口返回数据数量:', appointmentList.length, '🔥🔥🔥');
            console.log('PickingUpMapModePage: ✅ 接口返回数据详情:', JSON.stringify(appointmentList.slice(0, 2), null, 2));
            console.log('🔥 准备调用 setPickingUpData 更新本地状态');
            setPickingUpData(appointmentList);
            console.log('🔥 准备调用 setPickupTaskAppointmentList 更新 store');
            setPickupTaskAppointmentList(appointmentList);
            console.log('🔥 数据更新完成（本地状态 + store）');
        } catch (error: any) {
            // 忽略 AbortError 和用户中止请求的错误
            if (
                error.name === 'AbortError' || 
                error.message?.includes('aborted') ||
                error.message?.includes('user aborted') ||
                error.message?.toLowerCase().includes('abort')
            ) {
                console.log('🔥 请求被中止（正常情况，组件可能已卸载）');
                return;
            }
            console.error('🔥🔥🔥 PickingUpMapModePage: ❌ 获取数据失败:', error, '🔥🔥🔥');
            console.error('错误详情:', {
                name: error.name,
                message: error.message,
                stack: error.stack
            });
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
                console.log('PickingUpMapModePage: ✅ loading 设置为 false');
            }
        }
    }, []);

    // 组件挂载时立即获取数据
    useEffect(() => {
        console.log('PickingUpMapModePage: 🚀 useEffect 触发 - 组件挂载，准备获取数据');
        
        const abortController = new AbortController();
        fetchPickupTaskAppointmentList(abortController.signal);
        
        return () => {
            console.log('PickingUpMapModePage: 🔚 组件卸载，取消请求');
            abortController.abort();
        };
    }, [fetchPickupTaskAppointmentList]);

    // 监听 pickingUpData 变化
    useEffect(() => {
        console.log('PickingUpMapModePage: 📊 pickingUpData 已更新，当前长度:', pickingUpData.length);
    }, [pickingUpData]);

    
    // 控制返回行为，返回到揽收列表页面
    useHistoryControl("/pickup/picking-up", {
        useReplace: true,
        controlNavButton: false
    });

    // 在组件挂载时检查进入来源
    const [isFromDeliveringList] = useState(() => {
        console.log('PickingUpMapModePage: 🚀 useEffect 触发 - 检查进入来源');
        const entrySource = sessionStorage.getItem('mapModeEntrySource');
        if (entrySource === 'delivering-list') {
            sessionStorage.removeItem('mapModeEntrySource');
            return true;
        }
        return false;
    });

    // Popup 控制状态
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedMarkerData, setSelectedMarkerData] = useState<any>(null);
    const pickingUpDetailCardRef = useRef<PickingUpDetailCardRef>(null);

    useEffect(() => {
        console.log('PickingUpMapModePage: 🔄 数据过滤 useEffect 触发, pickingUpData.length:', pickingUpData.length);
        if (pickingUpData.length > 0) {
            const filteredData = pickingUpData.filter(appointment => 
                !appointment.isFinished && 
                appointment.status !== 'CANCELLED' && 
                appointment.status !== 'TRANSFER_OUT'
            );
            console.log('PickingUpMapModePage: 🔄 过滤后数据数量:', filteredData.length);
            console.log('PickingUpMapModePage: 🔄 过滤后数据:', filteredData.map(d => ({ id: d.id, status: d.status, isFinished: d.isFinished })));
            setPickingUpDataList(filteredData);
        } else {
            console.log('PickingUpMapModePage: ⚠️ pickingUpData 为空，不进行过滤');
        }
    }, [pickingUpData]);

    // 处理配送地址数据
    const deliveryAddresses = useMemo(() => {
        console.log('PickingUpMapModePage: 🗺️ 计算 deliveryAddresses, pickingUpDataList.length:', pickingUpDataList.length);
        const addresses = pickingUpDataList.map((appointment, index) => ({
            id: appointment.id,
            address: buildAddressFromAppointment(appointment),
            trackingNumber: appointment.appointmentNumber || '',
            deliveryTaskOrderId: parseInt(appointment.taskId),
            stopNumber: getStopNumberDisplay(appointment),
            subStopNumber: "0",
            isAbnormal: appointment.isAbnormal,
            localMarkerColor: DEFAULT_MARKER_COLOR,
            latitude: appointment.latitude ?? undefined,
            longitude: appointment.longitude ?? undefined
        }));
        console.log('PickingUpMapModePage: 🗺️ deliveryAddresses 计算完成，数量:', addresses.length);
        return addresses;
    }, [pickingUpDataList]);

    // 地图引用，用于直接操作地图
    const mapRef = useRef<any>(null);

    // 保存地图状态到 sessionStorage
    const saveMapState = useCallback(() => {
        if (mapRef.current) {
            const state = {
                center: [mapRef.current.getCenter().lng, mapRef.current.getCenter().lat],
                zoom: mapRef.current.getZoom(),
                timestamp: Date.now()
            };
            sessionStorage.setItem('mapModePageState', JSON.stringify(state));
            console.log('Saved map state:', state);
        }
    }, []);

    // 获取保存的地图状态
    const getSavedMapState = () => {
        if (isFromDeliveringList) {
            return null;
        }
        const savedState = sessionStorage.getItem('mapModePageState');
        if (savedState) {
            try {
                const state = JSON.parse(savedState);
                return state;
            } catch (error) {
                console.warn('Failed to parse saved map state:', error);
            }
        }
        return null;
    };

    // 处理标记点击事件 - 跳转到订单详情页面
    const handleMarkerClick = async (markerData: any) => {
        console.log('🔥 handleMarkerClick 触发, markerData.id:', markerData.id);
        console.log('🔥 当前 pickingUpDataList 长度:', pickingUpDataList.length);
        console.log('🔥 pickingUpDataList 所有 ID:', pickingUpDataList.map(item => item.id));
        
        const appointment = pickingUpDataList.find(item => item.id === markerData.id);
        console.log('🔥 找到的 appointment:', appointment ? '找到了' : '未找到');
        console.log('🔥 appointment 详情:', appointment);
        
        // 调用 getPickupTaskAppointmentStatus 接口检查状态
        try {
            const { data } = await client.query<GetPickupTaskAppointmentStatusResponse>({
                query: GET_PICKUP_TASK_APPOINTMENT_STATUS,
                variables: { appointmentId: appointment?.id ,taskId : appointment?.taskId },
                fetchPolicy: 'network-only',
                errorPolicy: 'all'
            });
            console.log('🔥 getPickupTaskAppointmentStatus 返回结果:', data.getPickupTaskAppointmentStatus);
            
            // 如果状态是 CANCELLED 或 TRANSFER_OUT，弹出警告提示
            if (data.getPickupTaskAppointmentStatus === 'CANCELLED' || data.getPickupTaskAppointmentStatus === 'TRANSFER_OUT') {
                Toast.show({
                    position: 'bottom',
                    content: t('pages.pickUp.pickingUp.appointmentCancelledOrRemoved'),
                    duration: 2000
                });
                // 刷新地图页面数据
                await fetchPickupTaskAppointmentList();
                return;
            }
        } catch (error: any) {
            // 忽略用户中止请求的错误
            if (
                error.name === 'AbortError' || 
                error.message?.includes('aborted') ||
                error.message?.includes('user aborted') ||
                error.message?.toLowerCase().includes('abort')
            ) {
                console.log('🔥 getPickupTaskAppointmentStatus 请求被中止');
                return;
            }
            console.error('🔥 调用 getPickupTaskAppointmentStatus 失败:', error);
        }
        
        setSelectedMarkerData(appointment);
        setPopupVisible(true);
        // 关闭长按导航弹窗
        pickingUpDetailCardRef.current?.closeLongPressModal();
    };


    // 处理地图点击事件 - 关闭弹窗和长按导航弹窗
    const handleMapClick = () => {
        setPopupVisible(false);
        // 关闭长按导航弹窗
        pickingUpDetailCardRef.current?.closeLongPressModal();
    };

    // 按钮点击前的回调 - 保存地图状态
    const handleBeforeNavigate = useCallback(() => {
        console.log('按钮点击，即将跳转，保存当前地图状态');
        saveMapState();
    }, [saveMapState]);

    console.log('PickingUpMapModePage: 🎨 准备渲染, pickingUpDataList.length:', pickingUpDataList.length, 'loading:', loading);

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Navbar - 固定在顶部 */}
            <NavBar onBack={() => navigate(-1)}>
                {t('pages.pickUp.pickingUp.mapTitle')}
            </NavBar>

            {/* 地图容器 - 占据剩余空间 */}
            <div className="flex-1 relative">
                {pickingUpDataList.length > 0 ? (
                    <>
                        {console.log('PickingUpMapModePage: 🗺️ 渲染地图组件')}
                        <MapboxMap
                            deliveryAddresses={deliveryAddresses}
                            onMarkerClick={handleMarkerClick}
                            onMapClick={handleMapClick}
                            mapRef={mapRef}
                            getSavedMapState={getSavedMapState}
                            pageMarker={MapBoxPageMarker.PICKUP_MAP_MODE}
                        />
                    </>
                ) : (
                    <>
                        {console.log('PickingUpMapModePage: ⚠️ 渲染空状态')}
                        <EmptyState description={t(`pages.pickUp.noPickUpInfo`) ?? ''} />
                    </>
                )}
            </div>

            {/* Popup 弹窗 */}
            <Popup
                visible={popupVisible}
                position="bottom"
                mask={false}
                bodyStyle={{
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    height: '50vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {(() => {
                    console.log('🔥 Popup 渲染, visible:', popupVisible, 'selectedMarkerData:', selectedMarkerData ? '有数据' : '无数据');
                    if (selectedMarkerData) {
                        console.log('🔥 渲染 PickingUpDetailCard, appointmentId:', selectedMarkerData.id, 'status:', selectedMarkerData.status);
                        return (
                            <PickingUpDetailCard
                                ref={pickingUpDetailCardRef}
                                appointmentId={selectedMarkerData.id}
                                status={selectedMarkerData.status}
                                fromMapMode={true}
                                onClose={() => setPopupVisible(false)}
                                onBeforeNavigate={handleBeforeNavigate}
                            />
                        );
                    } else {
                        console.log('🔥 Popup 打开但 selectedMarkerData 为空');
                        return <div>无数据</div>;
                    }
                })()}
            </Popup>
        </div>
    );
}

export default PickingUpMapModePage